<template>
    <div class="absolute bottom-0 left-0 right-0 pb-8 z-40">
        <!-- Caption - performers, relative date and title -->
        <div class="px-4 mb-4 max-w-[calc(100%-5rem)] pr-16">
            <div v-if="performers.length" class="flex items-center gap-2 mb-1">
                <p class="text-white/70 text-sm font-medium drop-shadow-lg">
                    <template v-for="(performer, index) in performers" :key="performer.key">
                        <span v-if="index" class="text-white/40">, </span>
                        <a v-if="performer.href"
                            :href="performer.href"
                            @click.stop
                            class="text-white/70 no-underline hover:text-white hover:underline underline-offset-2 transition-colors"
                        >{{ performer.name }}</a>
                        <span v-else>{{ performer.name }}</span>
                    </template>
                </p>
            </div>

            <h2 v-if="videoData?.title" class="text-white text-base font-bold drop-shadow-lg line-clamp-2 mb-2">
                {{ videoData.title }}
            </h2>

            <!-- Member-created clips carry a creator credit; imported clips have none. -->
            <p v-if="creator" class="text-white/70 text-sm font-medium drop-shadow-lg mb-2">
                <span class="text-white/40">by </span>
                <a v-if="creator.href"
                    :href="creator.href"
                    @click.stop
                    class="text-white/70 no-underline hover:text-white hover:underline underline-offset-2 transition-colors"
                >{{ creator.name }}</a>
                <span v-else>{{ creator.name }}</span>
            </p>

            <!-- Fantasy chips — only clip-curated categories; never on the SFW feed. -->
            <div v-if="!isSfwFeed && fantasies.length" class="flex flex-wrap items-center gap-2 mt-3">
                <a v-for="fantasy in fantasies"
                    :key="fantasy.key"
                    :href="fantasy.href"
                    @click.stop
                    class="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white/80 text-xs font-medium no-underline hover:bg-white/20 hover:text-white transition drop-shadow-lg"
                >#{{ fantasy.name }}</a>
            </div>

            <!-- Member-created tag chips — their own guard, so a clip with only member
                 tags never renders an empty fantasy row above it. -->
            <div v-if="!isSfwFeed && memberTags.length" class="flex flex-wrap items-center gap-2 mt-2">
                <a v-for="tag in memberTags"
                    :key="tag.key"
                    :href="tag.href"
                    @click.stop
                    class="px-2.5 py-1 rounded-full bg-transparent backdrop-blur-md border border-dashed border-white/25 text-white/60 text-xs font-medium no-underline hover:bg-white/10 hover:text-white transition drop-shadow-lg"
                >#{{ tag.name }}</a>
            </div>
        </div>

        <!-- Mute toggle + seek bar -->
        <div v-if="!shouldVideoBeLocked" class="px-4 pr-16 flex items-center gap-3">
            <button
                type="button"
                @click.stop="$emit('toggleMute')"
                class="flex-shrink-0 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-black/50 transition"
                :aria-label="isMuted ? 'Unmute' : 'Mute'"
            >
                <VolumeX v-if="isMuted" class="w-5 h-5 text-white drop-shadow-lg" />
                <Volume2 v-else class="w-5 h-5 text-white drop-shadow-lg" />
            </button>
            <!-- HLS quality override: the feed only passes levels on local and staging. -->
            <div v-if="qualityLevels.length > 1" ref="qualityMenuRef" class="relative flex-shrink-0">
                <button
                    type="button"
                    @click.stop="isQualityMenuOpen = !isQualityMenuOpen"
                    @keydown.esc.stop="isQualityMenuOpen = false"
                    class="h-8 px-3 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center gap-1.5 text-white text-xs font-semibold hover:bg-black/50 transition"
                    aria-haspopup="menu"
                    :aria-expanded="isQualityMenuOpen"
                    :aria-label="`Quality: ${qualityLabel}`"
                >
                    <Settings2 class="w-3.5 h-3.5" />
                    <span>{{ qualityLabel }}</span>
                </button>
                <div
                    v-if="isQualityMenuOpen"
                    role="menu"
                    aria-label="Quality"
                    class="absolute bottom-full left-0 mb-2 min-w-[7rem] py-1 rounded-xl bg-black/80 backdrop-blur-md border border-white/10 shadow-lg"
                    @keydown.esc.stop="isQualityMenuOpen = false"
                >
                    <button
                        v-for="option in qualityOptions"
                        :key="option.label"
                        type="button"
                        role="menuitemradio"
                        :aria-checked="option.height === preferredHeight"
                        @click.stop="selectQuality(option.height)"
                        class="w-full px-3 py-2 flex items-center justify-between gap-3 text-left text-sm text-white/80 hover:bg-white/10 hover:text-white"
                        :class="{ 'text-white font-semibold': option.height === preferredHeight }"
                    >
                        <span>{{ option.label }}</span>
                        <Check v-if="option.height === preferredHeight" class="w-4 h-4" />
                    </button>
                </div>
            </div>
            <!-- Up/Down are left alone: the feed uses them to change clips. -->
            <div
                ref="progressBarRef"
                role="slider"
                tabindex="0"
                aria-label="Seek"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round(displayProgress)"
                :aria-valuetext="`${Math.round(displayProgress)}%`"
                @mousedown="handleProgressMouseDown"
                @touchstart="handleProgressTouchStart"
                @keydown.left.prevent.stop="seekBy(-SEEK_KEY_STEP)"
                @keydown.right.prevent.stop="seekBy(SEEK_KEY_STEP)"
                @keydown.home.prevent.stop="$emit('seek', 0)"
                @keydown.end.prevent.stop="$emit('seek', 100)"
                class="relative flex-1 h-1 bg-white/30 rounded-full overflow-visible cursor-pointer group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                :class="{ 'h-1.5': isDragging }"
            >
                <div
                    class="h-full bg-white rounded-full"
                    :class="{ 'transition-none': isDragging, 'transition-all duration-100': !isDragging }"
                    :style="{ width: `${displayProgress}%` }"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { Check, Settings2, Volume2, VolumeX } from 'lucide-vue-next'
import { usePage } from '@inertiajs/vue3'
import { onClickOutside } from '@vueuse/core'

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    progress: {
        type: Number,
        default: 0
    },
    isMuted: {
        type: Boolean,
        default: false
    },
    shouldVideoBeLocked: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String,
        default: ''
    },
    // On the SFW feed the performer link stays inside the sfw route family and the
    // fantasy chips are not rendered at all.
    isSfwFeed: {
        type: Boolean,
        default: false
    },
    // Rendition heights of the current clip's hls.js stream; empty hides the quality picker.
    qualityLevels: {
        type: Array,
        default: () => []
    },
    // The height hls.js is playing right now.
    activeHeight: {
        type: Number,
        default: null
    },
    // The viewer's override; null is Auto.
    preferredHeight: {
        type: Number,
        default: null
    },
})

const emit = defineEmits(['toggleMute', 'seek', 'share', 'selectQuality'])

const qualityMenuRef = ref(null)
const isQualityMenuOpen = ref(false)

onClickOutside(qualityMenuRef, () => {
    isQualityMenuOpen.value = false
})

const qualityOptions = computed(() => [
    { label: 'Auto', height: null },
    ...props.qualityLevels.map((height) => ({ label: `${height}p`, height })),
])

// Same reading as the scene page's pill: "Auto", "720p · Auto" once ABR picks a rung, "720p" pinned.
const qualityLabel = computed(() => {
    if (props.preferredHeight !== null) {
        return `${props.activeHeight ?? props.preferredHeight}p`
    }

    return props.activeHeight ? `${props.activeHeight}p · Auto` : 'Auto'
})

const selectQuality = (height) => {
    isQualityMenuOpen.value = false
    emit('selectQuality', height)
}

const progressBarRef = ref(null)
const isDragging = ref(false)
const dragProgress = ref(0) // Local progress while dragging

const page = usePage()

// The page prop is the fallback so the overlay keeps linking within the current host even
// when it is mounted without an explicit routePrefix.
const routePrefix = computed(() => props.routePrefix || page.props?.routePrefix || 'www')

// The filtered-feed routes are registered per prefix; a missing one must not take the
// overlay down with it, so an unresolvable name simply yields no href.
const safeRoute = (name, params) => {
    try {
        return route(routePrefix.value + name, params)
    } catch (e) {
        return null
    }
}

const feedRouteName = (suffix) =>
    '.verticalClips.' + (props.isSfwFeed ? 'sfw.' : '') + 'feed' + suffix

const performerFeedUrl = (slug) => safeRoute(feedRouteName('.performer'), { performer: slug })

const fantasyFeedUrl = (slug) => safeRoute(feedRouteName('.fantasy'), { fantasy: slug })

const tagFeedUrl = (slug) => safeRoute(feedRouteName('.tag'), { tag: slug })

const memberFeedUrl = (slug) => safeRoute(feedRouteName('.member'), { member: slug })

// null for encoder-imported clips, which is every clip that predates member submissions.
const creator = computed(() => {
    const creator = props.videoData?.creator

    if (!creator?.name) {
        return null
    }

    return {
        name: creator.name,
        href: creator.slug ? memberFeedUrl(creator.slug) : null,
    }
})

const performers = computed(() => {
    const performers = props.videoData?.performers

    if (!Array.isArray(performers)) {
        return []
    }

    return performers
        .filter((performer) => performer?.name)
        .map((performer) => ({
            key: performer.id ?? performer.slug ?? performer.name,
            name: performer.name,
            href: performer.slug ? performerFeedUrl(performer.slug) : null,
        }))
})

// Chips only exist as an entry point into a filtered feed, so drop any we cannot link.
const fantasies = computed(() => {
    const fantasies = props.videoData?.fantasies

    if (props.isSfwFeed || !Array.isArray(fantasies)) {
        return []
    }

    return fantasies
        .filter((fantasy) => fantasy?.name && fantasy?.slug)
        .map((fantasy) => ({
            key: fantasy.id ?? fantasy.slug,
            name: fantasy.name,
            href: fantasyFeedUrl(fantasy.slug),
        }))
        .filter((fantasy) => fantasy.href)
})

// The member-created half of the vocabulary. Same "drop anything we cannot link" rule as
// the fantasy chips, and the same silence on the sfw feed, which renders no chips at all.
const memberTags = computed(() => {
    const tags = props.videoData?.member_tags

    if (props.isSfwFeed || !Array.isArray(tags)) {
        return []
    }

    return tags
        .filter((tag) => tag?.name && tag?.slug)
        .map((tag) => ({
            key: tag.id ?? tag.slug,
            name: tag.name,
            href: tagFeedUrl(tag.slug),
        }))
        .filter((tag) => tag.href)
})

const displayProgress = computed(() => (isDragging.value ? dragProgress.value : props.progress))

// Percent of the clip per arrow press, the same unit `seek` already carries.
const SEEK_KEY_STEP = 5

const seekBy = (delta) => {
    emit('seek', Math.max(0, Math.min(100, props.progress + delta)))
}

// Mouse events for desktop
const handleProgressMouseDown = (e) => {
    isDragging.value = true
    dragProgress.value = calculateProgress(e.clientX)

    document.addEventListener('mousemove', handleProgressMouseMove)
    document.addEventListener('mouseup', handleProgressMouseUp)
}

const handleProgressMouseMove = (e) => {
    if (isDragging.value) {
        dragProgress.value = calculateProgress(e.clientX)
    }
}

const handleProgressMouseUp = () => {
    if (isDragging.value) {
        emit('seek', dragProgress.value)
        isDragging.value = false
    }
    document.removeEventListener('mousemove', handleProgressMouseMove)
    document.removeEventListener('mouseup', handleProgressMouseUp)
}

// Touch events for mobile
const handleProgressTouchStart = (e) => {
    isDragging.value = true
    const touch = e.touches[0]
    dragProgress.value = calculateProgress(touch.clientX)

    document.addEventListener('touchmove', handleProgressTouchMove, { passive: false })
    document.addEventListener('touchend', handleProgressTouchEnd)
}

const handleProgressTouchMove = (e) => {
    if (isDragging.value) {
        e.preventDefault() // Prevent scrolling while dragging
        const touch = e.touches[0]
        dragProgress.value = calculateProgress(touch.clientX)
    }
}

const handleProgressTouchEnd = () => {
    if (isDragging.value) {
        emit('seek', dragProgress.value)
        isDragging.value = false
    }
    document.removeEventListener('touchmove', handleProgressTouchMove)
    document.removeEventListener('touchend', handleProgressTouchEnd)
}

// The overlay is behind a v-if on the lock state, so it can unmount mid-drag, before the
// mouseup/touchend that would otherwise remove these.
onBeforeUnmount(() => {
    document.removeEventListener('mousemove', handleProgressMouseMove)
    document.removeEventListener('mouseup', handleProgressMouseUp)
    document.removeEventListener('touchmove', handleProgressTouchMove)
    document.removeEventListener('touchend', handleProgressTouchEnd)
})

const calculateProgress = (clientX) => {
    if (!progressBarRef.value) return 0

    const rect = progressBarRef.value.getBoundingClientRect()
    const x = clientX - rect.left
    return Math.max(0, Math.min(100, (x / rect.width) * 100))
}
</script>
